import { DEFAULT_VIBE, VIBE_NAMES, applyTheme, vibeWebfont } from "./index";
import type { ThemeMode, ThemePreference, VibeName } from "./types";

/**
 * Light / Dark / System, with System as the default.
 *
 * Three values, so a two-state toggle cannot express it: "System" is not "whichever
 * one it currently resolves to", it is a standing instruction to follow the OS.
 *
 * The preference is per account. There is no field for it on the API yet, so it lives
 * in localStorage under the member's id; swapping this for a request means changing
 * `read` and `write` and nothing else.
 */

const KEY = "s4s-theme";
const VIBE_KEY = "s4s-vibe";
/** The pre-paint script in index.html reads this. Keep the two in step. */
const RESOLVED_KEY = "s4s-theme-resolved";

export const THEME_PREFERENCES: ThemePreference[] = ["light", "dark", "system"];
export const VIBES = VIBE_NAMES;

const isPreference = (value: any): value is ThemePreference => THEME_PREFERENCES.includes(value);
const isVibe = (value: any): value is VibeName => VIBE_NAMES.includes(value);

function storageKey(key: string, userId?: string | null): string {
  return userId ? `${key}:${userId}` : key;
}

export function readPreference(userId?: string | null): ThemePreference {
  try {
    const stored = localStorage.getItem(storageKey(KEY, userId)) ?? localStorage.getItem(KEY);
    return isPreference(stored) ? stored : "system";
  } catch {
    return "system";
  }
}

export function readVibe(userId?: string | null): VibeName {
  try {
    const stored = localStorage.getItem(storageKey(VIBE_KEY, userId)) ?? localStorage.getItem(VIBE_KEY);
    return isVibe(stored) ? stored : DEFAULT_VIBE;
  } catch {
    return DEFAULT_VIBE;
  }
}

function write(key: string, value: string, userId?: string | null) {
  try {
    localStorage.setItem(storageKey(key, userId), value);
    // Also under the bare key: the pre-paint script runs before the member is known.
    localStorage.setItem(key, value);
  } catch {
    /* private mode, or storage disabled - the preference just will not survive */
  }
}

const systemQuery = () =>
  typeof window !== "undefined" && window.matchMedia
    ? window.matchMedia("(prefers-color-scheme: light)")
    : null;

export function resolveMode(preference: ThemePreference): ThemeMode {
  if (preference !== "system") return preference;
  return systemQuery()?.matches ? "light" : "dark";
}

let current: ThemePreference = "system";
let currentVibe: VibeName = DEFAULT_VIBE;
const listeners = new Set<() => void>();

/**
 * A vibe that draws in a face the others do not fetches it the first time it is
 * chosen, so nobody downloads a serif they will never see.
 */
function loadVibeWebfont(vibe: VibeName) {
  const href = vibeWebfont(vibe);
  if (!href || typeof document === "undefined") return;
  if (document.querySelector(`link[data-vibe-font="${vibe}"]`)) return;
  const link = document.createElement("link");
  link.rel = "stylesheet";
  link.href = href;
  link.dataset.vibeFont = vibe;
  document.head.appendChild(link);
}

/** Apply, cache the ground colour for the next load's pre-paint, and notify. */
function paint() {
  loadVibeWebfont(currentVibe);
  const mode = resolveMode(current);
  const theme = applyTheme(mode, currentVibe);
  try {
    localStorage.setItem(RESOLVED_KEY, JSON.stringify({ mode, ground: theme.color.ground }));
  } catch {
    /* ignore */
  }
  listeners.forEach((listener) => listener());
}

export function initThemeMode(userId?: string | null) {
  current = readPreference(userId);
  currentVibe = readVibe(userId);
  paint();

  // Following the OS means following it as it changes, not only at load.
  const query = systemQuery();
  query?.addEventListener?.("change", () => {
    if (current === "system") paint();
  });
}

export function setThemePreference(preference: ThemePreference, userId?: string | null) {
  current = preference;
  write(KEY, preference, userId);
  paint();
}

export function setVibe(vibe: VibeName, userId?: string | null) {
  currentVibe = vibe;
  write(VIBE_KEY, vibe, userId);
  paint();
}

export function getThemePreference(): ThemePreference {
  return current;
}

export function getVibe(): VibeName {
  return currentVibe;
}

export function subscribeThemePreference(listener: () => void) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}
